import { useState, useEffect, useCallback, useRef } from 'react'
import { WordLength, Alert } from '../types/game'
import { GAME_MODES, ALERT_TIME } from '../constants/gameConfig'
import { challengeApi } from '../services/challengeApi'
import {
  getGuestName,
  markAnagramModeAsPlayed,
  saveAnagramCompletedDetail,
  recordTimeSpent,
} from '../utils/storage'
import {
  trackAnagramGameStart,
  trackAnagramGameCompletion,
} from '../services/gameAnalytics'

interface AnagramAttempt {
  attempt: string
  correct: boolean
}

export interface AnagramTile {
  id: string
  letter: string
}

interface UseAnagramProps {
  mode: WordLength
}

interface UseAnagramReturn {
  tiles: AnagramTile[]
  setTiles: (tiles: AnagramTile[]) => void
  attemptHistory: AnagramAttempt[]
  attemptCount: number
  submitAttempt: () => Promise<void>
  gameOver: boolean
  won: boolean
  secondsTaken: number | null
  targetWord: string
  definition: string
  setterNote?: unknown
  isLoading: boolean
  error: string | null
  sessionId: string | null
  alerts: Alert[]
}

/**
 * Mirrors useWordle.ts's session lifecycle (init -> submit -> win handling)
 * but with Anagram-shaped local state - no Tile[][] board, no keyboard
 * color states, no guess-limit countdown (Anagram allows unlimited
 * attempts). useWordle.ts itself is not touched or reused directly.
 */
export const useAnagram = ({ mode }: UseAnagramProps): UseAnagramReturn => {
  const gameStartTimeRef = useRef<number>(Date.now())
  const gameLanguage = GAME_MODES[mode].language

  const [tiles, setTiles] = useState<AnagramTile[]>([])
  const [attemptHistory, setAttemptHistory] = useState<AnagramAttempt[]>([])
  const [attemptCount, setAttemptCount] = useState(0)
  const [sessionId, setSessionId] = useState<string | null>(null)
  // Starts true (not false) because the initial session-load effect runs
  // after the first paint - defaulting to false left a real, visible frame
  // where isLoading was false and tiles was still [], which made
  // AnagramGamePage's `isLoading && tiles.length === 0` loading guard
  // skip straight to the empty "ready" UI before the fetch had returned.
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [gameOver, setGameOver] = useState(false)
  const [won, setWon] = useState(false)
  const [secondsTaken, setSecondsTaken] = useState<number | null>(null)
  const [targetWord, setTargetWord] = useState('')
  const [definition, setDefinition] = useState('')
  const [setterNote, setSetterNote] = useState<unknown>(null)
  const [alerts, setAlerts] = useState<Alert[]>([])

  const addAlert = useCallback((message: string, duration = 1000) => {
    const id = Math.random().toString()
    setAlerts((prev) => [...prev, { message, duration, id }])
    if (duration) {
      setTimeout(() => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== id))
      }, duration)
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const init = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const config = await challengeApi.startChallenge({
          challengeType: 'anagram',
          mode: 'daily',
          length: mode,
          language: gameLanguage.code,
          guestName: getGuestName() ?? undefined,
        })
        if (cancelled) return

        const scrambled = (config.payload?.scrambled as string[]) || []
        setSessionId(config.sessionId)
        setTiles(scrambled.map((letter, i) => ({ id: `${i}-${letter}`, letter })))
        setAttemptCount((config.payload?.attemptCount as number) || 0)

        gameStartTimeRef.current = Date.now()
        try {
          trackAnagramGameStart(mode)
        } catch (trackError) {
          console.error('Failed to track anagram game start:', trackError)
        }

        if (config.status !== 'active') {
          setGameOver(true)
          setWon(config.status === 'won')
          if (config.targetWord) setTargetWord(config.targetWord)
          if (config.definition) setDefinition(config.definition)
          if (config.setterNote) setSetterNote(config.setterNote)
          if (config.targetWord) {
            saveAnagramCompletedDetail(mode, config.targetWord, config.status === 'won')
          }
          // Backfills a session this client never observed completing live
          // (new device, cleared local storage) - no-ops if this mode was
          // already recorded today, e.g. by the live win path above.
          if (typeof config.totalTimeSeconds === 'number') {
            recordTimeSpent(config.totalTimeSeconds, `anagram-${mode}`)
          }
        }
      } catch (err) {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Connection required to play'
        setError(message)
        addAlert('Connection required to play.', ALERT_TIME.ERROR)
        setGameOver(true)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    init()

    return () => {
      cancelled = true
    }
  }, [mode, gameLanguage, addAlert])

  const submitAttempt = useCallback(async () => {
    if (gameOver || isLoading || !sessionId || tiles.length === 0) return

    const attempt = tiles.map((tile) => tile.letter).join('')

    setIsLoading(true)
    try {
      const result = await challengeApi.submitGuess(sessionId, attempt)
      const correct = Boolean(result.payload?.correct)
      const newAttemptCount = (result.payload?.attemptCount as number) ?? attemptCount + 1

      setAttemptHistory((prev) => [...prev, { attempt, correct }])
      setAttemptCount(newAttemptCount)

      if (!correct) {
        addAlert('Not quite - try again', ALERT_TIME.ERROR)
        return
      }

      setGameOver(true)
      setWon(true)
      if (result.targetWord) setTargetWord(result.targetWord)
      if (result.definition) setDefinition(result.definition)
      if (result.setterNote) setSetterNote(result.setterNote)

      markAnagramModeAsPlayed(mode)
      saveAnagramCompletedDetail(mode, result.targetWord ?? attempt, true)

      const timeTaken = Date.now() - gameStartTimeRef.current
      const secondsTakenValue = Math.round(timeTaken / 1000)
      setSecondsTaken(secondsTakenValue)
      recordTimeSpent(secondsTakenValue, `anagram-${mode}`)

      try {
        trackAnagramGameCompletion(mode, true, newAttemptCount, timeTaken)
      } catch (trackError) {
        console.error('Failed to track anagram game completion:', trackError)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error submitting attempt'
      setError(message)
      addAlert(message, ALERT_TIME.ERROR)
    } finally {
      setIsLoading(false)
    }
  }, [gameOver, isLoading, sessionId, tiles, attemptCount, mode, addAlert])

  return {
    tiles,
    setTiles,
    attemptHistory,
    attemptCount,
    submitAttempt,
    gameOver,
    won,
    secondsTaken,
    targetWord,
    definition,
    setterNote,
    isLoading,
    error,
    sessionId,
    alerts,
  }
}
